// Installed Devices → Browse: a user's estate as THEY see it, drilled down
// User → Location → Room → Device, with "All" at every level.
//
// Everything here is asked of the SERVER for the picked user's id, never the
// local cache — the cache holds only the session user's mappings, so a phone
// answering from it would show the admin's own view labelled as somebody
// else's. The per-user endpoints are the same ones the home screen uses; only
// the local-first wrapper is skipped (getDevicesByRoomFresh).
//
// A "row" at each level carries the rows above it (location on a room, both on
// a device) so a flat "All locations → All rooms" list can still say where each
// piece is.

import { post, getDevicesByRoomFresh } from '../api/api';
import { sortByOrder } from '../utils/sortOrder';

const rows = (res) => (Array.isArray(res && res.data) ? res.data : []);

export const locationName = (l) => l.LocationName || l.LocationWithName || `Location ${l.LocationID}`;
export const roomName = (r) => r.RoomName || `Room ${r.RoomID}`;
export const deviceName = (d) => d.DeviceName || d.UniqueNumber || 'Device';

// GetLocationsByUserId — 3 columns (LocationName, LocationID, PlaceName), in
// the customer's SortOrder.
export async function locationsOfUser(userId) {
  const res = await post('GetLocationsByUserId', { UserId: userId });
  return sortByOrder(rows(res));
}

// One location's rooms, each stamped with its location so the flat list can
// label it.
export async function roomsOfLocation(userId, location) {
  const res = await post('GetRoomsbyUserIDandLocationId', {
    UserId: userId, LocationID: location.LocationID,
  });
  return sortByOrder(rows(res)).map((r) => ({ ...r, location }));
}

// Every room across the given locations, location order then room order.
export async function roomsOfLocations(userId, locations) {
  const lists = await Promise.all(
    locations.map((l) => roomsOfLocation(userId, l).catch(() => [])),
  );
  return lists.flat();
}

// One room's devices — the three-call fetch (room + location + DeviceIDs
// 3/19/21) so nothing the home screen would show is missed here.
export async function devicesOfRoom(userId, room) {
  const res = await getDevicesByRoomFresh(userId, room.RoomID, room.location.LocationID);
  return sortByOrder(rows(res)).map((d) => ({ ...d, room, location: room.location }));
}

// Every device across the given rooms, in room order.
export async function devicesOfRooms(userId, roomList) {
  const lists = await Promise.all(
    roomList.map((r) => devicesOfRoom(userId, r).catch(() => [])),
  );
  return lists.flat();
}
